import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Settings,
  Layers,
  AlertCircle,
} from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import toast from 'react-hot-toast';
import { createMap, createTileLayer, calculateMapBounds, MAP_STYLES, fitMapBoundsAsHome } from '../../../utils/mapUtils';
import { initializeFuelColors, getFuelColor as getFuelColorFromCache } from '../../../utils/fuelColors';
import { transmissionAPI, gasPipelinesAPI } from '../../../services/api';
import { parseLinestringGeometry } from '../../../utils/wktUtils';
import {
  clearGasPipelineLayerGroups,
  GAS_PIPELINE_STATUS_COLORS,
  GAS_PIPELINES_MASTER_LAYER_KEY,
  getGasLayerKey,
  getGasStatusLayerKeys,
  gasPipelineLayersNeedBuild,
  isGasLayerVisible,
  isGasPipelineLayerKey,
} from '../../../utils/gasPipelineLayers';
import { smartFormatNumber } from '../../../utils/numberFormatting';
import TransmissionLineDocumentationAccordion from './subComponents/transmissionLine/TransmissionLineDocumentationAccordion';
import LoadingState from './subComponents/transmissionLine/states/LoadingState';
import NoTransmissionDataState from './subComponents/transmissionLine/states/NoTransmissionDataState';
import ErrorState from './subComponents/transmissionLine/states/ErrorState';
import TransmissionLineMapCard from './subComponents/transmissionLine/TransmissionLineMapCard';
import TransmissionLineCapacityMapLegend from './subComponents/transmissionLine/TransmissionLineCapacityMapLegend';
import {
  bringTransmissionCapacityLayersToFront,
  CAPACITY_FILTERED_LAYER_KEY,
  clearTransmissionCapacityLayerGroups,
  computeTransmissionLineCapacityBins,
  formatBelowP25LegendLabel,
  formatNullExcludedLegendLabel,
  getAllCapacityLayerKeys,
  getLineRenderStyle,
  isTransmissionCapacityLayerKey,
  logTransmissionCapacityBinningDiagnostics,
  TRANSMISSION_CAPACITY_UNIT,
  FILTERED_LINE_COLOR,
} from '../../../utils/transmissionLineCapacity';

// Configuration constants
const CONFIG = {
  colors: {
    region: [
      '#E7A396', '#EACE84', '#BAB9E1', '#D9A1C0', '#F7A978', '#F0ACB7', '#F8E5EB', '#EBCFB2',
      '#DDE4BE', '#B0E3CD', '#BDD4F6', '#CCCBF2', '#CAD892', '#969A60', '#758D46', '#98D0F5',
      '#5E9DBE', '#3C8782', '#EB5A6D', '#F3C9E4', '#EEADA7', '#BDBDBD', '#F9F4BC', '#FAF5AF',
      '#AD9281', '#F2C6C7', '#EB7757', '#ED6C84', '#83A061', '#A0BA46'
    ],
    voltage: {
      high: '#d62728',    // Red for 380kV+
      medium: '#ff7f0e',  // Orange for 220kV
      low: '#2ca02c',     // Green for 110kV
      default: '#7f7f7f'  // Gray for lower voltages
    },
    transmission: {
      bus: '#fbbf24',
      busStroke: '#f59e0b',
      ntc: '#666'
    }
  },
  markers: {
    population: { radius: 4, weight: 1, opacity: 0.8, fillOpacity: 0.6 },
    cluster: { radius: 6, weight: 2, opacity: 1, fillOpacity: 0.8 },
    transmission: { radius: 6, weight: 0, opacity: 1, fillOpacity: 0.8 },
    powerPlant: { weight: 2, opacity: 1, fillOpacity: 0.8 }
  },
  lines: {
    transmission: { weight: 1, opacity: 1, smoothFactor: 0, lineCap: 'round' },
    ntc: { weight: 2, opacity: 0.6, dashArray: '5, 5' }
  },
  powerPlantSizes: {
    large: 12,    // >= 1000 MW
    medium: 10,   // >= 500 MW
    small: 8,     // >= 100 MW
    tiny: 6       // < 100 MW
  }
};

const normalizeFuelType = (fuelType) => {
  if (!fuelType) return 'unknown';
  return String(fuelType)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'unknown';
};

const getFuelLayerKey = (fuelType) => `powerPlants__${normalizeFuelType(fuelType)}`;

/** Baseline cluster counts for the Transmission Lines dropdown; merged with DB default per ISO. */
const BASE_CLUSTER_OPTIONS = [6, 8, 10, 12, 15, 20];

const TRANSMISSION_MAP_HEADER_DIVIDER =
  '<div class="vs-transmission-map-divider" role="presentation"></div>';

const LAYER_VISIBILITY_ROW_CLASS =
  'layer-visibility-row grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 pr-4 md:gap-x-8 md:pr-12 py-1 cursor-pointer text-left';

const LayerVisibilityRow = ({ checked, onToggle, onContextMenu, ariaLabel, className = '', children }) => (
  <label
    className={`${LAYER_VISIBILITY_ROW_CLASS} ${className}`.trim()}
    onContextMenu={onContextMenu}
    aria-label={ariaLabel}
  >
    <div className="flex items-center gap-2 min-w-0">
      {children}
    </div>
    <input
      type="checkbox"
      checked={checked}
      onChange={onToggle}
      className="checkbox-kanors shrink-0"
      tabIndex={-1}
      aria-hidden="true"
    />
  </label>
);

const TransmissionLineTab = ({ countryIso, isTabActive = true }) => {
  const [transmissionData, setTransmissionData] = useState(null);
  const [networkData, setNetworkData] = useState(null);
  const [generationData, setGenerationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const [clusters, setClusters] = useState(12);
  const [clusterOptions, setClusterOptions] = useState(BASE_CLUSTER_OPTIONS);
  const [mapStyle, setMapStyle] = useState(MAP_STYLES.CLEAN_MINIMAL);
  const [populationRange, setPopulationRange] = useState({ min: 0, max: 1 }); // For scaling circles
  const { summary } = transmissionData || {};
  const networkStats = networkData?.statistics || {};
  const generationStats = generationData?.statistics || {};
  const availableFuelTypes = useMemo(() => {
    if (!generationData?.plants?.length) return [];
    return Array.from(
      new Set(generationData.plants.map(plant => plant.fuel_type).filter(Boolean))
    ).sort((a, b) => String(a).localeCompare(String(b)));
  }, [generationData]);
  const fuelSubtypeLayerKeys = useMemo(
    () => availableFuelTypes.map(fuelType => getFuelLayerKey(fuelType)),
    [availableFuelTypes]
  );

  const capacityBinSpec = useMemo(
    () => computeTransmissionLineCapacityBins(networkData?.lines),
    [networkData?.lines]
  );

  const capacityLineLayerKeys = useMemo(() => {
    const keys = capacityBinSpec.bins?.map((bin) => bin.layerKey) ?? [];
    if ((capacityBinSpec.excludedGreyCount ?? 0) > 0) {
      keys.push(CAPACITY_FILTERED_LAYER_KEY);
    }
    return keys;
  }, [capacityBinSpec]);

  useEffect(() => {
    if (!countryIso || !capacityBinSpec) return;
    logTransmissionCapacityBinningDiagnostics(countryIso, capacityBinSpec);
  }, [countryIso, capacityBinSpec]);
  
  // Layer visibility controls - will be updated when network data loads
  const [layerVisibility, setLayerVisibility] = useState({
    populationPoints: true,
    clusterCenters: false, // Hidden by default
    transmissionBuses: true, // Visible by default
    powerPlants: true, // Visible by default
    [GAS_PIPELINES_MASTER_LAYER_KEY]: false,
  });
  const [gasPipelinesData, setGasPipelinesData] = useState(null);
  const [gasLoading, setGasLoading] = useState(false);
  const gasFetchedIsoRef = useRef(null);
  const gasLayersBuiltForIsoRef = useRef(null);
 

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const layerGroupsRef = useRef({});
  const clusterGroupsDataRef = useRef(null); // Store cluster groups data for zoom-based switching
  const demandClusterDefaultsCountryRef = useRef(null);
  const lastCompletedTransmissionLoadKeyRef = useRef('');
  const activeTransmissionLoadIsoRef = useRef(null);

  // Second map references
  const mapRef2 = useRef(null);
  const mapInstanceRef2 = useRef(null);
  const layerGroupsRef2 = useRef({});
  
  // Haversine formula: Calculate distance between two geographic points
  const calculateDistance = useCallback((point1, point2) => {
    const r = 6371; // Earth's radius in kilometers
    const dLat = ((point2.lat - point1.lat) * Math.PI) / 180;
    const dLng = ((point2.lng - point1.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((point1.lat * Math.PI) / 180) *
      Math.cos((point2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return r * c;
  }, []);

  // Get clustering distance based on map zoom level (increased for more aggressive clustering)
  const getClusteringDistanceByZoom = useCallback((zoom) => {
    if (zoom >= 1 && zoom <= 3) return 400;   // Global/Continental view (increased from 200)
    if (zoom >= 4 && zoom <= 6) return 200;   // Country/Regional view (increased from 100)
    if (zoom >= 7 && zoom <= 8) return 75;    // State/City view (increased from 25)
    if (zoom >= 9 && zoom <= 10) return 40;  // District view (increased from 15, extended to zoom 10)
    if (zoom >= 11 && zoom <= 12) return 20;  // Local view (added clustering for zoom 11-12)
    return 0;                                  // Very local view (no clustering at zoom 13+)
  }, []);

  // Cluster points by geographic distance using Haversine formula
  const clusterByDistance = useCallback((points, maxDistance) => {
    if (maxDistance === 0) {
      // Return individual clusters (no clustering)
      return points.map((point) => [point]);
    }

    const clusters = [];
    const visited = new Set();

    for (let i = 0; i < points.length; i++) {
      if (visited.has(i)) continue;

      const cluster = [points[i]];
      visited.add(i);

      for (let j = i + 1; j < points.length; j++) {
        if (visited.has(j)) continue;

        const distance = calculateDistance(points[i], points[j]);
        if (distance <= maxDistance) {
          cluster.push(points[j]);
          visited.add(j);
        }
      }

      clusters.push(cluster);
    }

    return clusters;
  }, [calculateDistance]);

  // Cluster points by distance WITHIN same original cluster only (strict cluster boundaries)
  const clusterByDistanceWithinClusters = useCallback((points, maxDistance) => {
    if (maxDistance === 0) {
      // Return individual clusters (no clustering)
      return points.map((point) => [point]);
    }

    // First, group points by their original cluster ID
    const pointsByOriginalCluster = {};
    points.forEach(point => {
      const originalClusterId = point.cluster !== undefined ? point.cluster : 'unknown';
      if (!pointsByOriginalCluster[originalClusterId]) {
        pointsByOriginalCluster[originalClusterId] = [];
      }
      pointsByOriginalCluster[originalClusterId].push(point);
    });

    // Then, apply distance-based clustering within each original cluster separately
    const allClusters = [];
    
    Object.keys(pointsByOriginalCluster).forEach(originalClusterId => {
      const pointsInCluster = pointsByOriginalCluster[originalClusterId];
      
      // Apply distance clustering only within this original cluster
      const distanceClusters = clusterByDistance(pointsInCluster, maxDistance);
      
      // Add all distance clusters from this original cluster
      allClusters.push(...distanceClusters);
    });

    return allClusters;
  }, [clusterByDistance]);

  useEffect(() => {
    initializeFuelColors().catch(error => {
      console.error('Failed to initialize fuel colors:', error);
    });
  }, []);

  // Utility functions for colors and styling
  const getRegionColor = useCallback((clusterId) => {
    return CONFIG.colors.region[clusterId % CONFIG.colors.region.length];
  }, []);

  const getFuelColor = useCallback((fuelType) => {
    const serviceColor = getFuelColorFromCache(fuelType);
    if (serviceColor && serviceColor !== '#7F8C8D') {
      return serviceColor;
    }
    return CONFIG.colors.fuel[fuelType] || CONFIG.colors.fuel.unknown;
  }, []);

  const getMarkerSize = useCallback((capacity) => {
    if (capacity >= 1000) return CONFIG.powerPlantSizes.large;
    if (capacity >= 500) return CONFIG.powerPlantSizes.medium;
    if (capacity >= 100) return CONFIG.powerPlantSizes.small;
    return CONFIG.powerPlantSizes.tiny;
  }, []);

  // Calculate population range for scaling (per country)
  // IMPORTANT: Clusters have NO role in scaling. Each circle's population is taken independently.
  // Uses 15th percentile for min (filters small outliers) and 99th percentile for max (filters extreme outliers).
  // Using high percentile ensures proper differentiation while filtering extreme outliers.
  const calculatePopulationRange = useCallback((demandPoints) => {
    if (!demandPoints || demandPoints.length === 0) {
      console.warn('calculatePopulationRange: No demand points provided');
      return { min: 0, max: 1 };
    }

    // Extract population from EVERY point, regardless of cluster assignment
    const allPopulations = demandPoints.map(point => ({
      raw_weight: point.raw_weight,
      weight: point.weight,
      extracted: point.raw_weight || point.weight || 0
    }));
    
    const populations = allPopulations
      .map(p => p.extracted)
      .filter(pop => pop > 0)
      .sort((a, b) => a - b); // Sort ascending for percentile calculation

    if (populations.length === 0) {
      console.warn('calculatePopulationRange: No valid populations found! Returning default range.');
      return { min: 0, max: 1 };
    }

    // Use 15th percentile for min (to filter out very small outliers)
    // Use 99th percentile for max (to filter extreme outliers while capturing high-end values)
    const percentile15Index = Math.floor(populations.length * 0.15);
    const percentile99Index = Math.ceil(populations.length * 0.99) - 1;
    
    const min = populations[percentile15Index] || populations[0];
    const max = populations[percentile99Index] || populations[populations.length - 1];
    
    return { min, max };
  }, []);

  // Calculate range for grouped clusters based on their total populations
  const calculateGroupedClusterRange = useCallback((clusters) => {
    if (!clusters || clusters.length === 0) {
      return { min: 0, max: 1 };
    }
    
    // Extract total populations from grouped clusters (clusters with more than 1 point)
    const groupedClusters = clusters.filter(c => c.length > 1);
    if (groupedClusters.length === 0) {
      return { min: 0, max: 1 };
    }
    
    const totalPopulations = groupedClusters.map(cluster => {
      return cluster.reduce((sum, point) => sum + (point.raw_weight || point.weight || 0), 0);
    }).filter(pop => pop > 0).sort((a, b) => a - b);
    
    if (totalPopulations.length === 0) {
      return { min: 0, max: 1 };
    }
    
    // Use 15th percentile for min and 99th percentile for max (for grouped clusters)
    const percentile15Index = Math.floor(totalPopulations.length * 0.15);
    const percentile99Index = Math.ceil(totalPopulations.length * 0.99) - 1;
    
    const min = totalPopulations[percentile15Index] || totalPopulations[0];
    const max = totalPopulations[percentile99Index] || totalPopulations[totalPopulations.length - 1];
    
    return { min, max };
  }, []);

  // Scale population circle radius using square root scaling
  // Each circle is scaled independently based on its own population value
  // Clusters are NOT used in this calculation - only individual point population
  // Uses 2px-22px range for better visual distinction
  const getScaledPopulationRadius = useCallback((population, range = null) => {
    const effectiveRange = range || populationRange;
    const { min, max } = effectiveRange;
    const minRadius = 2;  // Minimum visible circle size
    const maxRadius = 22; // Maximum circle size

    // If no valid range or invalid population, return minimum
    if (!effectiveRange || max === 0 || population <= 0 || !isFinite(population)) {
      return minRadius;
    }

    // If all populations are the same, return minimum
    if (max === min) {
      return minRadius;
    }

    // Logarithmic scaling: better for wide population ranges, provides better differentiation at high values
    // Uses individual point's population, normalized against 15th-99th percentile range
    // Normalized will be in [0, 1] range (values above 99th percentile are clamped to 1)
    let normalized = (population - min) / (max - min);
    
    // Clamp to [0, 1] range
    normalized = Math.max(0, Math.min(1, normalized));
    
    // Logarithmic scaling: log(1 + normalized * 9) / log(10)
    // This maps [0,1] to [0,1] with better differentiation at high values
    // For normalized=0: log(1)/log(10) = 0
    // For normalized=1: log(10)/log(10) = 1
    // Logarithmic scaling provides better visual distinction for high populations
    const logNormalized = Math.log(1 + normalized * 9) / Math.log(10);
    
    // Scale from minRadius to maxRadius using logarithmic normalization
    const radius = minRadius + (maxRadius - minRadius) * logNormalized;

    return Math.round(radius);
  }, [populationRange]);

  const gasStatusLayerKeys = useMemo(
    () => getGasStatusLayerKeys(gasPipelinesData?.statistics),
    [gasPipelinesData?.statistics]
  );

  const gasPipelineStats = gasPipelinesData?.statistics || {};

  // Marker creation utilities
  const createMarker = useCallback((lat, lng, config, popupContent, pane = null) => {
    const markerOptions = { ...config };
    // Only set pane if it exists and is valid
    if (pane) {
      // Use overlayPane (default for markers) if custom pane doesn't work
      // Custom panes might not render circle markers correctly
      markerOptions.pane = pane;
    }
    const marker = L.circleMarker([lat, lng], markerOptions);
    if (popupContent) {
      marker.bindPopup(popupContent);
    }
    return marker;
  }, []);

  const transmissionBusMarkersRef = useRef([]);
  const transmissionBusMarkersRef2 = useRef([]);

  const createTransmissionBusMarker = useCallback((bus, pane = 'transmissionBusPane') => {
    const config = {
      radius: 4,
      fillColor: '#dc2626',
      color: '#dc2626',
      weight: 1,
      opacity: 1,
      fillOpacity: 0  // Transparent fill, only border visible
    };
    
    const popupContent = `
      <div class="p-2">
        <h3 class="font-semibold text-sm">${bus.name}</h3>
        ${TRANSMISSION_MAP_HEADER_DIVIDER}
        <p class="text-xs text-gray-600">Type: Transmission Bus</p>
        <p class="text-xs text-gray-600">Voltage: ${bus.voltage} kV</p>
      </div>
    `;
    
    const marker = createMarker(bus.lat, bus.lng, config, popupContent, pane);
    if (marker) {
      marker.options.baseRadius = config.radius;
    }
    return marker;
  }, [createMarker]);

  const createPopulationMarker = useCallback((point, range = null, pane = 'populationPane') => {
    const color = getRegionColor(point.cluster);
    const population = point.raw_weight || point.weight || 0;
    
    // Use provided range or fallback to state
    const effectiveRange = range || populationRange;
    const scaledRadius = getScaledPopulationRadius(population, effectiveRange);
    
    const config = {
      radius: scaledRadius,
      fillColor: color,
      color: color,
      weight: CONFIG.markers.population.weight,
      opacity: CONFIG.markers.population.opacity,
      fillOpacity: CONFIG.markers.population.fillOpacity
    };
    
    const popupContent = `
      <div class="p-2">
        <h3 class="font-semibold text-sm">${point.name}</h3>
        ${TRANSMISSION_MAP_HEADER_DIVIDER}
        <p class="text-xs text-gray-600">Population: ${population > 0 ? smartFormatNumber(population) : 'N/A'}</p>
        <p class="text-xs text-gray-600">Region: ${point.cluster}</p>
        <p class="text-xs text-gray-500">Circle Size: ${scaledRadius}px</p>
      </div>
    `;
    
    return createMarker(point.lat, point.lng, config, popupContent, pane);
  }, [createMarker, getRegionColor, getScaledPopulationRadius, populationRange]);

  // Create grouped cluster marker - aggregates all points in a cluster
  const createGroupedClusterMarker = useCallback((clusterData, range = null, pane = 'populationPane') => {
    const { clusterId, centerLat, centerLng, totalPopulation, pointCount, points } = clusterData;
    // All points have the same original cluster ID (strict boundaries enforced)
    const color = getRegionColor(clusterId);
    
    // Use provided range or fallback to state
    const effectiveRange = range || populationRange;
    // Scale based on aggregated total population
    const scaledRadius = getScaledPopulationRadius(totalPopulation, effectiveRange);
    
    const config = {
      radius: scaledRadius,
      fillColor: color,
      color: color,
      weight: CONFIG.markers.population.weight + 1, // Slightly thicker border for grouped markers
      opacity: CONFIG.markers.population.opacity,
      fillOpacity: CONFIG.markers.population.fillOpacity
    };
    
    // Create popup with cluster summary
    const topCities = points
      .sort((a, b) => (b.raw_weight || b.weight || 0) - (a.raw_weight || a.weight || 0))
      .slice(0, 5)
      .map(p => `${p.name} (${smartFormatNumber(p.raw_weight || p.weight || 0)})`)
      .join(', ');
    
    const popupContent = `
      <div class="p-2">
        <h3 class="font-semibold text-sm">Distance Cluster (Cluster ${clusterId})</h3>
        ${TRANSMISSION_MAP_HEADER_DIVIDER}
        <p class="text-xs text-gray-600">Total Population: ${smartFormatNumber(totalPopulation)}</p>
        <p class="text-xs text-gray-600">Cities: ${pointCount}</p>
        <p class="text-xs text-gray-500">Circle Size: ${scaledRadius}px</p>
        <p class="text-xs text-gray-400 mt-1">All cities from original cluster ${clusterId} (strict boundaries)</p>
        ${topCities ? `<p class="text-xs text-gray-400 mt-1">Top cities: ${topCities}</p>` : ''}
      </div>
    `;
    
    return createMarker(centerLat, centerLng, config, popupContent, pane);
  }, [createMarker, getRegionColor, getScaledPopulationRadius, populationRange]);

  const createPowerPlantMarker = useCallback((plant) => {
    const config = {
      radius: getMarkerSize(plant.capacity_mw),
      fillColor: getFuelColor(plant.fuel_type),
      color: "transparent",
      weight: CONFIG.markers.powerPlant.weight,
      opacity: CONFIG.markers.powerPlant.opacity,
      fillOpacity: CONFIG.markers.powerPlant.fillOpacity
    };
    
    const popupContent = `
      <div class="p-2">
        <h3 class="font-semibold text-sm">${plant.name}</h3>
        ${TRANSMISSION_MAP_HEADER_DIVIDER}
        <p class="text-xs text-gray-600">Capacity: ${smartFormatNumber(plant.capacity_mw)} MW</p>
        <p class="text-xs text-gray-600">Fuel Type: ${plant.fuel_type}</p>
        <p class="text-xs text-gray-600">Bus ID: ${plant.bus_id || 'N/A'}</p>
        ${plant.description ? `<p class="text-xs text-gray-500 mt-1">${plant.description}</p>` : ''}
      </div>
    `;
    
    const marker = createMarker(plant.lat, plant.lng, config, popupContent);
    if (marker) {
      marker.options.baseRadius = config.radius;
    }
    return marker;
  }, [createMarker, getMarkerSize, getFuelColor]);

  // Helper function to load transmission infrastructure (buses and lines)
  // Order: Lines first (will be bottom), then buses (will be on top of lines)
  // But we want lines on top, so we'll add buses first, then lines
  const loadTransmissionInfrastructure = useCallback((mapInstance, layerGroups, data, busMarkersRef = null, binSpec = capacityBinSpec) => {
    if (!data) return;

    clearTransmissionCapacityLayerGroups(layerGroups);
    if (layerGroups.transmissionBuses) {
      layerGroups.transmissionBuses.clearLayers();
    }

    // Ensure transmission panes exist with correct z-index (above population circles)
    if (mapInstance) {
      // Transmission buses: z-index 500 (middle, above population)
      if (!mapInstance.getPane('transmissionBusPane')) {
        mapInstance.createPane('transmissionBusPane');
        mapInstance.getPane('transmissionBusPane').style.zIndex = 500;
      }
      
      // Transmission lines: z-index 1000 (top of vector layers; tooltips use tooltipPane above this)
      if (!mapInstance.getPane('transmissionLinePane')) {
        mapInstance.createPane('transmissionLinePane');
        mapInstance.getPane('transmissionLinePane').style.zIndex = 1000;
      }

      const tooltipPane = mapInstance.getPane('tooltipPane');
      if (tooltipPane) {
        tooltipPane.style.zIndex = 7500;
      }
    }

    // Add transmission buses first (middle layer)
    if (busMarkersRef) {
      busMarkersRef.current = [];
    }

    if (data.buses) {
      data.buses.forEach(bus => {
        if (bus.lat && bus.lng) {
          const marker = createTransmissionBusMarker(bus, 'transmissionBusPane');
          if (marker) {
            layerGroups.transmissionBuses.addLayer(marker);
            if (busMarkersRef) {
              busMarkersRef.current.push(marker);
            }
          }
        }
      });
    }

    // Add transmission lines: short/null first, capacity-colored on top
    if (data.lines) {
      const renderLine = (line, style) => {
        if (!line.bus0_lat || !line.bus0_lng || !line.bus1_lat || !line.bus1_lng) return;

        const voltage = line.voltage || 0;
        const capacityMva = line.capacity ?? line.s_nom;
        let coordinates;
        if (line.geometry) {
          const geometryCoords = parseLinestringGeometry(line.geometry);
          coordinates = geometryCoords?.length > 1
            ? geometryCoords
            : [[line.bus0_lat, line.bus0_lng], [line.bus1_lat, line.bus1_lng]];
        } else {
          coordinates = [[line.bus0_lat, line.bus0_lng], [line.bus1_lat, line.bus1_lng]];
        }

        const polyline = L.polyline(coordinates, {
          color: style.color,
          weight: CONFIG.lines.transmission.weight,
          opacity: style.opacity,
          pane: 'transmissionLinePane',
        });

        polyline.bindTooltip(
          `
            <div class="p-2">
              <h3 class="font-semibold text-sm">Transmission Line</h3>
              ${TRANSMISSION_MAP_HEADER_DIVIDER}
              <p class="text-xs text-gray-600">From: ${line.bus0_id}</p>
              <p class="text-xs text-gray-600">To: ${line.bus1_id}</p>
              <p class="text-xs text-gray-600">Voltage: ${voltage} kV</p>
              <p class="text-xs text-gray-600">Capacity: ${
                capacityMva != null && Number.isFinite(Number(capacityMva))
                  ? `${smartFormatNumber(capacityMva)} ${TRANSMISSION_CAPACITY_UNIT}`
                  : 'N/A'
              }</p>
              <p class="text-xs text-gray-600">Length: ${
                line.length != null && Number.isFinite(Number(line.length))
                  ? `${smartFormatNumber(line.length)} km`
                  : 'N/A'
              }</p>
            </div>
          `,
          {
            sticky: true,
            direction: 'auto',
            offset: [12, -12],
            opacity: 0.95,
            className: 'renewable-zone-tooltip',
          }
        );

        if (layerGroups[style.layerKey]) {
          layerGroups[style.layerKey].addLayer(polyline);
        }
      };

      data.lines.forEach((line) => {
        const style = getLineRenderStyle(line, binSpec, CONFIG.lines.transmission.opacity);
        if (style.layerKey === CAPACITY_FILTERED_LAYER_KEY) {
          renderLine(line, style);
        }
      });

      data.lines.forEach((line) => {
        const style = getLineRenderStyle(line, binSpec, CONFIG.lines.transmission.opacity);
        if (style.layerKey !== CAPACITY_FILTERED_LAYER_KEY) {
          renderLine(line, style);
        }
      });

      bringTransmissionCapacityLayersToFront(layerGroups, binSpec);
    }
  }, [capacityBinSpec, createTransmissionBusMarker]);

  const loadGasPipelinesOnMap = useCallback((mapInstance, layerGroups, data) => {
    if (!data?.pipelines?.length || !layerGroups) return;

    clearGasPipelineLayerGroups(layerGroups);

    let drawn = 0;
    data.pipelines.forEach((pipeline) => {
      const coords = parseLinestringGeometry(pipeline.geometry);
      if (!coords || coords.length < 2) return;

      const status = String(pipeline.status || 'unknown').toLowerCase();
      const layerKey = getGasLayerKey(status);
      if (!layerGroups[layerKey]) {
        layerGroups[layerKey] = L.layerGroup();
      }

      const color = GAS_PIPELINE_STATUS_COLORS[status] || '#34495e';
      const capacity = pipeline.capacity_bcm;
      const capacityLabel = capacity != null && Number.isFinite(Number(capacity))
        ? `${smartFormatNumber(capacity)} Bcm/y`
        : 'N/A';

      const polyline = L.polyline(coords, {
        color,
        weight: 3,
        opacity: 0.9,
      });

      polyline.bindTooltip(
        `
          <div class="vs-gas-pipeline-tooltip p-2">
            <h3 class="vs-gas-pipeline-tooltip__title font-semibold text-sm">${pipeline.project_name || pipeline.project_id}</h3>
            ${TRANSMISSION_MAP_HEADER_DIVIDER}
            <p class="vs-gas-pipeline-tooltip__field text-xs text-gray-600">Status: ${status}</p>
            <p class="vs-gas-pipeline-tooltip__field text-xs text-gray-600">Capacity: ${capacityLabel}</p>
            <p class="vs-gas-pipeline-tooltip__field text-xs text-gray-600">Countries: ${pipeline.countries_all || 'N/A'}</p>
            ${pipeline.start_year ? `<p class="vs-gas-pipeline-tooltip__field text-xs text-gray-600">Start year: ${pipeline.start_year}</p>` : ''}
          </div>
        `,
        {
          sticky: true,
          direction: 'auto',
          offset: [12, -12],
          opacity: 0.95,
          className: 'renewable-zone-tooltip gas-pipeline-tooltip',
        }
      );

      layerGroups[layerKey].addLayer(polyline);
      drawn += 1;
    });

    if (drawn === 0 && process.env.NODE_ENV === 'development') {
      const sample = data.pipelines[0]?.geometry;
      console.warn('[gas-pipelines] No polylines drawn', {
        pipelineCount: data.pipelines.length,
        sampleGeometryType: typeof sample,
        samplePreview: typeof sample === 'string' ? sample.slice(0, 120) : sample,
      });
    }
  }, []);

  const applyGasLayersToBothMaps = useCallback(() => {
    if (!gasPipelinesData) return;

    const buildForMap = (mapInstance, layerGroups) => {
      if (!mapInstance || !layerGroups) return;
      gasStatusLayerKeys.forEach((layerKey) => {
        if (!layerGroups[layerKey]) {
          layerGroups[layerKey] = L.layerGroup();
        }
      });
      loadGasPipelinesOnMap(mapInstance, layerGroups, gasPipelinesData);
    };

    buildForMap(mapInstanceRef.current, layerGroupsRef.current);
    buildForMap(mapInstanceRef2.current, layerGroupsRef2.current);
    gasLayersBuiltForIsoRef.current = countryIso;
  }, [countryIso, gasPipelinesData, gasStatusLayerKeys, loadGasPipelinesOnMap]);

  const updateTransmissionBusMarkerSizes = useCallback((mapInstance, busMarkersRef) => {
    if (!mapInstance || !busMarkersRef?.current?.length) return;

    const zoom = mapInstance.getZoom();
    const baseZoom = 7;
    const zoomDiff = zoom - baseZoom;
    const scale = Math.max(0.2, Math.min(2.2, Math.pow(1.3, zoomDiff)));

    busMarkersRef.current.forEach(marker => {
      if (marker && marker.options?.baseRadius) {
        marker.setRadius(marker.options.baseRadius * scale);
      }
    });
  }, []);

  // Render population markers based on zoom level using distance-based clustering
  const renderPopulationMarkers = useCallback((mapInstance, layerGroups, allPoints, range, zoom, networkData = null) => {
    layerGroups.populationPoints.clearLayers();
    
    // Ensure population pane exists and has correct z-index (below transmission lines)
    if (mapInstance && !mapInstance.getPane('populationPane')) {
      const populationPane = mapInstance.createPane('populationPane');
      populationPane.style.zIndex = 100;
      populationPane.style.pointerEvents = 'auto';
    }
    
    // Get clustering distance based on zoom level
    const maxDistance = getClusteringDistanceByZoom(zoom);
    
    if (maxDistance === 0) {
      // No clustering: Show individual population points
      // Temporarily remove pane to debug visibility issue
      allPoints.forEach(point => {
        const marker = createPopulationMarker(point, range, null); // Remove pane temporarily
        layerGroups.populationPoints.addLayer(marker);
      });
    } else {
      // Distance-based clustering: Group points within maxDistance, but ONLY within same original cluster
      // Strict cluster boundaries - no cross-cluster merging
      const clusters = clusterByDistanceWithinClusters(allPoints, maxDistance);
      
      // Calculate separate range for grouped clusters (based on total populations)
      const groupedClusterRange = calculateGroupedClusterRange(clusters);
      
      clusters.forEach((cluster, clusterIndex) => {
        if (cluster.length === 1) {
          // Single point cluster: show as individual marker (use individual point range)
          // Temporarily remove pane to debug visibility issue
          const marker = createPopulationMarker(cluster[0], range, null); // Remove pane temporarily
          layerGroups.populationPoints.addLayer(marker);
        } else {
          // Multiple points: create grouped cluster marker
          // Calculate cluster center and total population
          // Since we cluster within same original cluster, all points have the same cluster ID
          let sumLat = 0;
          let sumLng = 0;
          let totalPopulation = 0;
          
          cluster.forEach(point => {
            sumLat += point.lat;
            sumLng += point.lng;
            totalPopulation += (point.raw_weight || point.weight || 0);
          });
          
          // All points in this distance cluster have the same original cluster ID (strict boundaries)
          const originalClusterId = cluster[0].cluster !== undefined ? cluster[0].cluster : clusterIndex;
          
          const centerLat = sumLat / cluster.length;
          const centerLng = sumLng / cluster.length;
          
          const clusterData = {
            clusterId: originalClusterId, // All points have same cluster ID (strict boundaries)
            centerLat: centerLat,
            centerLng: centerLng,
            totalPopulation: totalPopulation,
            pointCount: cluster.length,
            points: cluster
          };
          
          // Create grouped cluster marker with scaled size based on total population
          // Use groupedClusterRange instead of individual point range for better differentiation
          // Temporarily remove pane to debug visibility issue
          const marker = createGroupedClusterMarker(clusterData, groupedClusterRange, null); // Remove pane temporarily
          layerGroups.populationPoints.addLayer(marker);
        }
      });
    }
    
    bringTransmissionCapacityLayersToFront(layerGroups, capacityBinSpec);
    if (layerGroups.transmissionBuses && typeof layerGroups.transmissionBuses.bringToFront === 'function') {
      layerGroups.transmissionBuses.bringToFront();
    }
  }, [createGroupedClusterMarker, createPopulationMarker, getClusteringDistanceByZoom, clusterByDistanceWithinClusters, calculateGroupedClusterRange, capacityBinSpec]);

  // Load demand cluster data (population, clusters + transmission)
  // Layer order (bottom to top): Demand points -> Transmission buses -> Transmission lines
  const loadDemandClusterData = useCallback((mapInstance, layerGroups) => {
    if (!mapInstance || !transmissionData) return;

    // Calculate population range for this dataset
    // IMPORTANT: Uses 15th-99th percentiles from ALL points
    const range = calculatePopulationRange(transmissionData.demand_points);
        
    // Clear existing data
    layerGroups.populationPoints.clearLayers();
    if (layerGroups.clusterCenters) {
      layerGroups.clusterCenters.clearLayers();
    }

    // Step 1: Get all valid points for distance-based clustering
    if (transmissionData.demand_points) {
      // Get all valid points (with lat/lng)
      const validPoints = transmissionData.demand_points.filter(
        p => p.lat && p.lng && p.lat !== null && p.lng !== null
      );
      
      // Store points for zoom-based switching
      clusterGroupsDataRef.current = validPoints;
      
      // Render based on current zoom level using distance-based clustering
      const currentZoom = mapInstance.getZoom();
      renderPopulationMarkers(mapInstance, layerGroups, validPoints, range, currentZoom, networkData);
    }

    // Step 2: Add cluster centers (also bottom layer, same level as demand points)
    if (transmissionData.cluster_centers) {
      transmissionData.cluster_centers.forEach((center, index) => {
        if (center.center_lat && center.center_lng) {
          const color = getRegionColor(center.cluster_id);
          
          const houseIcon = L.divIcon({
            className: 'custom-house-icon',
            html: `
              <div style="
                width: 12px; 
                height: 12px; 
                background-color: ${color}; 
                border: 2px solid white; 
                border-radius: 2px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 8px;
                color: white;
                font-weight: bold;
              ">
                ${center.cluster_id}
              </div>
            `,
            iconSize: [12, 12],
            iconAnchor: [6, 6]
          });

          const marker = L.marker([center.center_lat, center.center_lng], { icon: houseIcon });
          
          marker.bindPopup(`
            <div class="p-2">
              <h3 class="font-semibold text-sm">${center.name}</h3>
              ${TRANSMISSION_MAP_HEADER_DIVIDER}
              <p class="text-xs text-gray-600">Region ID: ${center.cluster_id}</p>
              <p class="text-xs text-gray-600">Total Demand: ${center.total_demand ? smartFormatNumber(center.total_demand) : 'N/A'}</p>
              <p class="text-xs text-gray-600">Cities: ${center.n_cities ? smartFormatNumber(center.n_cities) : 'N/A'}</p>
              <p class="text-xs text-gray-600">Major City: ${center.major_city || 'N/A'}</p>
            </div>
          `);

          layerGroups.clusterCenters.addLayer(marker);
        }
      });
    }

    // Step 3: Add transmission infrastructure (buses then lines)
    // This ensures buses are added before lines, so lines appear on top
    loadTransmissionInfrastructure(mapInstance, layerGroups, networkData, transmissionBusMarkersRef);
    updateTransmissionBusMarkerSizes(mapInstance, transmissionBusMarkersRef);
    
    // Bring transmission layers to front (above population circles)
    if (layerGroups.transmissionBuses && typeof layerGroups.transmissionBuses.bringToFront === 'function') {
      layerGroups.transmissionBuses.bringToFront();
    }
    bringTransmissionCapacityLayersToFront(layerGroups, capacityBinSpec);
  }, [transmissionData, networkData, capacityBinSpec, createGroupedClusterMarker, createPopulationMarker, renderPopulationMarkers, loadTransmissionInfrastructure, getRegionColor, updateTransmissionBusMarkerSizes, calculatePopulationRange, getScaledPopulationRadius, populationRange]);

  // Load generation cluster data (transmission + power plants)
  const powerPlantMarkersRef = useRef([]);

  const updatePowerPlantMarkerSizes = useCallback((mapInstance) => {
    if (!mapInstance || !powerPlantMarkersRef.current?.length) return;

    const zoom = mapInstance.getZoom();
    const baseZoom = 7;
    const zoomDiff = zoom - baseZoom;
    const scale = Math.max(0.15, Math.min(3, Math.pow(1.35, zoomDiff)));

    powerPlantMarkersRef.current.forEach(marker => {
      if (marker && marker.options?.baseRadius) {
        marker.setRadius(marker.options.baseRadius * scale);
      }
    });
  }, []);

  const loadGenerationClusterData = useCallback((mapInstance, layerGroups) => {
    if (!mapInstance) return;

    // Clear existing data
    Object.values(layerGroups).forEach(group => {
      group.clearLayers();
    });
    powerPlantMarkersRef.current = [];

    // Layer order (bottom to top): Power plants -> Transmission buses -> Transmission lines
    
    // Step 1: Add power plants first (middle layer, same level as buses)
    if (generationData && generationData.plants) {
      generationData.plants.forEach(plant => {
        if (plant.lat && plant.lng) {
          const marker = createPowerPlantMarker(plant);
          if (marker) {
            const fuelLayerKey = getFuelLayerKey(plant.fuel_type);
            if (layerGroups[fuelLayerKey]) {
              layerGroups[fuelLayerKey].addLayer(marker);
            } else {
              layerGroups.powerPlants.addLayer(marker);
            }
            powerPlantMarkersRef.current.push(marker);
          }
        }
      });
    }

    // Step 2: Add transmission infrastructure (buses then lines)
    // This ensures buses are added before lines, so lines appear on top
    loadTransmissionInfrastructure(mapInstance, layerGroups, networkData, transmissionBusMarkersRef2);
    
    updatePowerPlantMarkerSizes(mapInstance);
    updateTransmissionBusMarkerSizes(mapInstance, transmissionBusMarkersRef2);
    
    // Bring transmission layers to front (above power plants and circles)
    if (layerGroups.transmissionBuses && typeof layerGroups.transmissionBuses.bringToFront === 'function') {
      layerGroups.transmissionBuses.bringToFront();
    }
    bringTransmissionCapacityLayersToFront(layerGroups, capacityBinSpec);
  }, [networkData, generationData, capacityBinSpec, loadTransmissionInfrastructure, createPowerPlantMarker, updatePowerPlantMarkerSizes, updateTransmissionBusMarkerSizes]);

  const initializeMap = useCallback((mapRef, mapInstanceRef, layerGroupsRef, mapType = 'demand') => {
    if (!transmissionData || !transmissionData.demand_points || transmissionData.demand_points.length === 0) {
      return;
    }

    // Calculate map bounds from demand points
    const coordinates = transmissionData.demand_points.map(point => [point.lat, point.lng]);
    const bounds = calculateMapBounds(coordinates, 0.1);

    // Create map with clean styling
    const map = createMap(mapRef.current, {
      defaultStyle: 'CLEAN_MINIMAL',
      attributionControl: false
    });
    mapInstanceRef.current = map;

    // Create custom panes with z-index for proper layer ordering
    // Population circles: z-index 100 (bottom)
    if (!map.getPane('populationPane')) {
      const populationPane = map.createPane('populationPane');
      populationPane.style.zIndex = 100;
      populationPane.style.pointerEvents = 'auto';
    }
    
    // Transmission buses: z-index 500 (middle)
    if (!map.getPane('transmissionBusPane')) {
      const transmissionBusPane = map.createPane('transmissionBusPane');
      transmissionBusPane.style.zIndex = 500;
      transmissionBusPane.style.pointerEvents = 'auto';
    }
    
    // Transmission lines: z-index 1000 (top of vector layers; tooltips use tooltipPane above this)
    if (!map.getPane('transmissionLinePane')) {
      const transmissionLinePane = map.createPane('transmissionLinePane');
      transmissionLinePane.style.zIndex = 1000;
      transmissionLinePane.style.pointerEvents = 'auto';
    }

    const tooltipPane = map.getPane('tooltipPane');
    if (tooltipPane) {
      tooltipPane.style.zIndex = 7500;
    }

    // Fit bounds to data and store as reset "home" view
    fitMapBoundsAsHome(map, bounds, { padding: [5, 5] });

    // Initialize base layer groups
    layerGroupsRef.current = {
      populationPoints: L.layerGroup(),
      clusterCenters: L.layerGroup(),
      transmissionBuses: L.layerGroup(),
      powerPlants: L.layerGroup()
    };

    getAllCapacityLayerKeys(capacityBinSpec).forEach((layerKey) => {
      layerGroupsRef.current[layerKey] = L.layerGroup();
    });
    if (mapType === 'generation' && generationData?.plants?.length) {
      const uniqueFuelTypes = Array.from(
        new Set(generationData.plants.map(plant => plant.fuel_type).filter(Boolean))
      );
      uniqueFuelTypes.forEach(fuelType => {
        layerGroupsRef.current[getFuelLayerKey(fuelType)] = L.layerGroup();
      });
    }

    // Add layer groups to map in correct z-index order (bottom to top):
    // In Leaflet, layers added later appear on top
    // 1. Population points (bottom) - added first
    // 2. Cluster centers (same level as population points)
    // 3. Transmission buses (above circles) - added after population
    // 4. Transmission lines (top) - voltage layers added last
    // 5. Power plants (for generation map) - added last
    
    // Add population layers first (bottom layer)
    if (layerGroupsRef.current.populationPoints) {
      layerGroupsRef.current.populationPoints.addTo(map);
    }
    if (layerGroupsRef.current.clusterCenters) {
      layerGroupsRef.current.clusterCenters.addTo(map);
    }
    
    // Add transmission infrastructure AFTER population points (so it appears on top)
    if (layerGroupsRef.current.transmissionBuses) {
      layerGroupsRef.current.transmissionBuses.addTo(map);
    }
    
    getAllCapacityLayerKeys(capacityBinSpec).forEach((layerKey) => {
      if (layerGroupsRef.current[layerKey]) {
        layerGroupsRef.current[layerKey].addTo(map);
      }
    });
    
    // Add power plants last (for generation map)
    if (layerGroupsRef.current.powerPlants) {
      layerGroupsRef.current.powerPlants.addTo(map);
    }
    if (mapType === 'generation' && generationData?.plants?.length) {
      const uniqueFuelTypes = Array.from(
        new Set(generationData.plants.map(plant => plant.fuel_type).filter(Boolean))
      );
      uniqueFuelTypes.forEach(fuelType => {
        const fuelLayerKey = getFuelLayerKey(fuelType);
        if (layerGroupsRef.current[fuelLayerKey]) {
          layerGroupsRef.current[fuelLayerKey].addTo(map);
        }
      });
    }

    // Load data based on map type
    if (mapType === 'demand') {
      loadDemandClusterData(map, layerGroupsRef.current);
      
      // Ensure transmission layers are on top after loading
      if (layerGroupsRef.current.transmissionBuses && typeof layerGroupsRef.current.transmissionBuses.bringToFront === 'function') {
        layerGroupsRef.current.transmissionBuses.bringToFront();
      }
      bringTransmissionCapacityLayersToFront(layerGroupsRef.current, capacityBinSpec);
      
      // Add zoom event listener to switch between grouped and individual markers
      map.on('zoomend', () => {
        const currentZoom = map.getZoom();
        const range = calculatePopulationRange(transmissionData.demand_points);
        
        if (clusterGroupsDataRef.current) {
          renderPopulationMarkers(map, layerGroupsRef.current, clusterGroupsDataRef.current, range, currentZoom, networkData);
        }
        
        updateTransmissionBusMarkerSizes(map, transmissionBusMarkersRef);
        
        // Ensure transmission layers stay on top after zoom
        if (layerGroupsRef.current.transmissionBuses && typeof layerGroupsRef.current.transmissionBuses.bringToFront === 'function') {
          layerGroupsRef.current.transmissionBuses.bringToFront();
        }
        bringTransmissionCapacityLayersToFront(layerGroupsRef.current, capacityBinSpec);
      });
    } else if (mapType === 'generation') {
      loadGenerationClusterData(map, layerGroupsRef.current);
      
      // Ensure transmission layers are on top after loading
      if (layerGroupsRef.current.transmissionBuses && typeof layerGroupsRef.current.transmissionBuses.bringToFront === 'function') {
        layerGroupsRef.current.transmissionBuses.bringToFront();
      }
      bringTransmissionCapacityLayersToFront(layerGroupsRef.current, capacityBinSpec);
      
      map.on('zoomend', () => {
        updatePowerPlantMarkerSizes(map);
        updateTransmissionBusMarkerSizes(map, transmissionBusMarkersRef2);
        
        // Ensure transmission layers stay on top after zoom
        if (layerGroupsRef.current.transmissionBuses && typeof layerGroupsRef.current.transmissionBuses.bringToFront === 'function') {
          layerGroupsRef.current.transmissionBuses.bringToFront();
        }
        bringTransmissionCapacityLayersToFront(layerGroupsRef.current, capacityBinSpec);
      });
    }
  }, [transmissionData, generationData, capacityBinSpec, loadDemandClusterData, loadGenerationClusterData, networkData, updatePowerPlantMarkerSizes, updateTransmissionBusMarkerSizes, renderPopulationMarkers, calculatePopulationRange]);

  const initializeBothMaps = useCallback(() => {
    if (!transmissionData || !transmissionData.demand_points || transmissionData.demand_points.length === 0) {
      return;
    }

    // Initialize first map (Demand Cluster)
    initializeMap(mapRef, mapInstanceRef, layerGroupsRef, 'demand');
    
    // Initialize second map (Generation Cluster)
    initializeMap(mapRef2, mapInstanceRef2, layerGroupsRef2, 'generation');
  }, [transmissionData, initializeMap]);

  const updateLayerVisibility = useCallback(() => {
    // Layer configuration: which maps each layer should appear on
    const layerConfig = {
      populationPoints: ['demand'],
      clusterCenters: ['demand'],
      transmissionBuses: ['demand', 'generation'],
      powerPlants: ['generation'],
    };

    const gasMasterOn = layerVisibility[GAS_PIPELINES_MASTER_LAYER_KEY] !== false;

    const updateMapLayers = (mapInstance, layerGroups, mapType) => {
      if (!mapInstance) return;

      // Define z-index order (bottom to top)
      // In Leaflet, layers added later appear on top
      const zIndexOrder = [
        'populationPoints',    // Bottom - added first
        'clusterCenters',      // Bottom (same level as population)
        'transmissionBuses',   // Above circles - added after population
        'powerPlants'          // Middle (for generation map)
      ];

      // First, remove all layers
      Object.keys(layerVisibility).forEach(layerKey => {
        const group = layerGroups[layerKey];
        if (group) {
          const shouldShow = layerVisibility[layerKey] &&
                            (layerConfig[layerKey]?.includes(mapType) ||
                             (mapType === 'generation' && layerKey.startsWith('powerPlants__')) ||
                             isTransmissionCapacityLayerKey(layerKey) ||
                             isGasLayerVisible(layerVisibility, layerKey, gasMasterOn));

          if (!shouldShow) {
            group.remove();
          }
        }
      });

      Object.keys(layerGroups).forEach((layerKey) => {
        if (!isGasPipelineLayerKey(layerKey)) return;
        const group = layerGroups[layerKey];
        if (!group) return;
        const shouldShow = isGasLayerVisible(layerVisibility, layerKey, gasMasterOn);
        if (!shouldShow) {
          group.remove();
        }
      });

      // Then add layers back in correct z-index order (bottom to top)
      // Population layers first (bottom), then transmission layers (on top)
      zIndexOrder.forEach(layerKey => {
        const group = layerGroups[layerKey];
        if (group) {
          const shouldShow = layerVisibility[layerKey] && 
                            (layerConfig[layerKey]?.includes(mapType));
          
          if (shouldShow) {
            group.addTo(mapInstance);
          }
        }
      });

      // Add capacity-bin line layers and fuel subtypes — top layers, added last
      Object.keys(layerVisibility).forEach(layerKey => {
        if (layerKey.startsWith('powerPlants__')) {
          const group = layerGroups[layerKey];
          if (group && layerVisibility[layerKey] && mapType === 'generation') {
            group.addTo(mapInstance);
          }
        }
      });

      Object.keys(layerVisibility).forEach(layerKey => {
        if (isTransmissionCapacityLayerKey(layerKey)) {
          const group = layerGroups[layerKey];
          if (group && layerVisibility[layerKey]) {
            group.addTo(mapInstance);
          }
        }
      });

      Object.keys(layerGroups).forEach((layerKey) => {
        if (!isGasPipelineLayerKey(layerKey)) return;
        const group = layerGroups[layerKey];
        if (group && isGasLayerVisible(layerVisibility, layerKey, gasMasterOn)) {
          group.addTo(mapInstance);
        }
      });
      
      if (layerGroups.transmissionBuses && layerVisibility.transmissionBuses && typeof layerGroups.transmissionBuses.bringToFront === 'function') {
        layerGroups.transmissionBuses.bringToFront();
      }
      bringTransmissionCapacityLayersToFront(layerGroups, capacityBinSpec);
    };

    // Update both maps with appropriate data
    if (mapInstanceRef.current && layerGroupsRef.current) {
      updateMapLayers(mapInstanceRef.current, layerGroupsRef.current, 'demand');
    }
    if (mapInstanceRef2.current && layerGroupsRef2.current) {
      updateMapLayers(mapInstanceRef2.current, layerGroupsRef2.current, 'generation');
    }
  }, [layerVisibility, capacityBinSpec, gasStatusLayerKeys]);

  // Handle map style changes
  const handleMapStyleChange = useCallback((newStyle) => {
    setMapStyle(newStyle);
    
    // Find the style key for the selected style
    const styleKey = Object.keys(MAP_STYLES).find(key => MAP_STYLES[key].name === newStyle.name);
    
    // Update both maps if they exist
    const updateMapStyle = (mapInstance) => {
      if (mapInstance) {
        mapInstance.eachLayer((layer) => {
          if (layer instanceof L.TileLayer) {
            mapInstance.removeLayer(layer);
          }
        });
        const newTileLayer = createTileLayer(styleKey);
        newTileLayer.addTo(mapInstance);
      }
    };

    updateMapStyle(mapInstanceRef.current);
    updateMapStyle(mapInstanceRef2.current);
  }, []);

  const isActiveTransmissionLoad = useCallback(
    (iso) => activeTransmissionLoadIsoRef.current === (iso || '').toUpperCase(),
    []
  );

  // Reset maps/layers when country changes (gas cache cleared here too)
  useEffect(() => {
    const iso = (countryIso || '').toUpperCase();
    if (!iso) return;

    activeTransmissionLoadIsoRef.current = iso;
    setTransmissionData(null);
    setNetworkData(null);
    setGenerationData(null);
    setGenerationError(null);
    setError(null);
    setGasPipelinesData(null);
    gasFetchedIsoRef.current = null;
    gasLayersBuiltForIsoRef.current = null;
    clearGasPipelineLayerGroups(layerGroupsRef.current);
    clearGasPipelineLayerGroups(layerGroupsRef2.current);
    setLayerVisibility((prev) => {
      const next = { ...prev, [GAS_PIPELINES_MASTER_LAYER_KEY]: false };
      Object.keys(next).forEach((key) => {
        if (isGasPipelineLayerKey(key)) {
          delete next[key];
        }
      });
      return next;
    });
  }, [countryIso]);

  // Load transmission data (production: isActiveTransmissionLoad + cluster sync)
  useEffect(() => {
    const iso = (countryIso || '').toUpperCase();
    if (!iso) return;

    let cancelled = false;

    const loadAllData = async () => {
      try {
        setLoading(true);

        let clusterCount = clusters;

        if (demandClusterDefaultsCountryRef.current !== iso) {
          lastCompletedTransmissionLoadKeyRef.current = '';
          let nFromDb = 12;
          try {
            const def = await transmissionAPI.getTransmissionClusterDefault(iso);
            if (!cancelled && isActiveTransmissionLoad(iso) && def.success && def.data && def.data.n_clusters != null) {
              nFromDb = Number(def.data.n_clusters);
            }
          } catch {
            nFromDb = 12;
          }
          if (cancelled || !isActiveTransmissionLoad(iso)) return;
          clusterCount = nFromDb;
          demandClusterDefaultsCountryRef.current = iso;
          const merged = [...new Set([...BASE_CLUSTER_OPTIONS, clusterCount])].sort((a, b) => a - b);
          setClusterOptions(merged);
          lastCompletedTransmissionLoadKeyRef.current = '';
          // Sync cluster UI then load in the next effect run only — avoids two concurrent
          // Python transmission/data calls (first was causing 503 while Python was busy).
          if (clusters !== clusterCount) {
            setClusters(clusterCount);
            return;
          }
        }

        if (cancelled || !isActiveTransmissionLoad(iso)) return;

        const loadKey = `${iso}:${clusterCount}`;
        if (lastCompletedTransmissionLoadKeyRef.current === loadKey) {
          if (isActiveTransmissionLoad(iso)) {
            setLoading(false);
          }
          return;
        }

        // 1. Load demand/population data (Map 1) — critical: failure blocks entire tab
        const populationResponse = await transmissionAPI.getTransmissionData(iso, clusterCount);
        if (cancelled || !isActiveTransmissionLoad(iso)) return;

        if (!populationResponse.success) {
          throw new Error(populationResponse.error || 'Failed to load population data');
        }
        setTransmissionData(populationResponse.data);

        if (populationResponse.data?.demand_points) {
          const range = calculatePopulationRange(populationResponse.data.demand_points);
          setPopulationRange(range);
        }

        // 2 + 3: Generation data (Map 2) and network infrastructure (both maps) in parallel.
        const [generationResult, networkResult] = await Promise.allSettled([
          transmissionAPI.getTransmissionGenerationData(iso),
          transmissionAPI.getTransmissionNetworkData(iso),
        ]);

        if (cancelled || !isActiveTransmissionLoad(iso)) return;

        if (generationResult.status === 'fulfilled' && generationResult.value?.success) {
          setGenerationData(generationResult.value.data);
          setGenerationError(null);
        } else {
          const msg = generationResult.status === 'rejected'
            ? (generationResult.reason?.message || 'Network error')
            : (generationResult.value?.error || 'Generation data unavailable');
          setGenerationData(null);
          setGenerationError(msg);
        }

        if (networkResult.status === 'fulfilled' && networkResult.value?.success) {
          setNetworkData(networkResult.value.data);
        } else {
          setNetworkData(null);
        }

        lastCompletedTransmissionLoadKeyRef.current = loadKey;
        if (generationResult.status !== 'fulfilled' || !generationResult.value?.success) {
          toast.success('Demand data loaded (generation data unavailable)');
        } else {
          toast.success('All data loaded successfully');
        }
      } catch (err) {
        if (!cancelled && isActiveTransmissionLoad(iso)) {
          setError(err.message);
          toast.error(`Failed to load data: ${err.message}`);
          console.error('Error loading data:', err);
        }
      } finally {
        if (!cancelled && isActiveTransmissionLoad(iso)) {
          setLoading(false);
        }
      }
    };

    loadAllData();

    return () => {
      cancelled = true;
    };
  }, [countryIso, clusters, calculatePopulationRange, isActiveTransmissionLoad]);

  // Update layer visibility when network data loads (only once when data changes)
  useEffect(() => {
    if (!capacityLineLayerKeys.length) return;
    setLayerVisibility((prevVisibility) => {
      const newLayerVisibility = { ...prevVisibility };
      capacityLineLayerKeys.forEach((layerKey) => {
        if (!(layerKey in newLayerVisibility)) {
          newLayerVisibility[layerKey] = true;
        }
      });
      return newLayerVisibility;
    });
  }, [capacityLineLayerKeys]);

  useEffect(() => {
    if (!availableFuelTypes.length) return;
    setLayerVisibility(prevVisibility => {
      const newLayerVisibility = { ...prevVisibility };
      availableFuelTypes.forEach(fuelType => {
        const fuelKey = getFuelLayerKey(fuelType);
        if (!(fuelKey in newLayerVisibility)) {
          newLayerVisibility[fuelKey] = true;
        }
      });

      const visibleCount = availableFuelTypes.filter(
        fuelType => newLayerVisibility[getFuelLayerKey(fuelType)]
      ).length;
      newLayerVisibility.powerPlants = visibleCount > 0;
      return newLayerVisibility;
    });
  }, [availableFuelTypes]);

  useEffect(() => {
    if (!gasStatusLayerKeys.length) return;
    setLayerVisibility((prevVisibility) => {
      const next = { ...prevVisibility };
      gasStatusLayerKeys.forEach((layerKey) => {
        if (!(layerKey in next)) {
          next[layerKey] = true;
        }
      });
      return next;
    });
  }, [gasStatusLayerKeys]);

  useEffect(() => {
    // Guard on `!loading` so this fires (and finds refs set) when loading transitions
    // true→false, even if transmissionData was already committed in an earlier render
    // (which happens during the setClusters re-run on fresh mount).
    if (!loading && transmissionData && mapRef.current && mapRef2.current && !mapInstanceRef.current && !mapInstanceRef2.current) {
      initializeBothMaps();
    }
    
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current._vsMapInteraction?.destroy();
        mapInstanceRef.current._vsMapReset?.destroy();
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      if (mapInstanceRef2.current) {
        mapInstanceRef2.current._vsMapInteraction?.destroy();
        mapInstanceRef2.current._vsMapReset?.destroy();
        mapInstanceRef2.current.remove();
        mapInstanceRef2.current = null;
      }
    };
  }, [loading, transmissionData, initializeBothMaps]);

  // Maps are created once; reload layers when country/cluster data changes without remounting.
  useEffect(() => {
    if (loading || !transmissionData?.demand_points?.length) return;

    if (mapInstanceRef.current && layerGroupsRef.current) {
      loadDemandClusterData(mapInstanceRef.current, layerGroupsRef.current);
    }
    if (mapInstanceRef2.current && layerGroupsRef2.current) {
      loadGenerationClusterData(mapInstanceRef2.current, layerGroupsRef2.current);
    }
  }, [
    loading,
    countryIso,
    clusters,
    transmissionData,
    networkData,
    generationData,
    loadDemandClusterData,
    loadGenerationClusterData,
  ]);

  useEffect(() => {
    if (!mapInstanceRef.current && !mapInstanceRef2.current) return;

    const gasMasterOn = layerVisibility[GAS_PIPELINES_MASTER_LAYER_KEY] === true;
    if (gasPipelinesData && gasMasterOn) {
      const needsBuild =
        gasLayersBuiltForIsoRef.current !== countryIso ||
        gasPipelineLayersNeedBuild(layerGroupsRef.current, gasStatusLayerKeys) ||
        gasPipelineLayersNeedBuild(layerGroupsRef2.current, gasStatusLayerKeys);
      if (needsBuild) {
        applyGasLayersToBothMaps();
      }
    }
    updateLayerVisibility();
  }, [
    updateLayerVisibility,
    transmissionData,
    networkData,
    gasPipelinesData,
    layerVisibility,
    countryIso,
    applyGasLayersToBothMaps,
  ]);

  // Leaflet tiles/layout break when the panel was `display:none`; reflow when tab is shown again.
  useEffect(() => {
    if (!isTabActive) return undefined;
    const id = window.requestAnimationFrame(() => {
      [mapInstanceRef.current, mapInstanceRef2.current].forEach((map) => {
        if (!map) return;
        map.invalidateSize();
        if (typeof map.redraw === 'function') {
          map.redraw();
        }
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [isTabActive]);

  const toggleLayer = useCallback((layerKey) => {
    setLayerVisibility(prev => ({
      ...prev,
      [layerKey]: !prev[layerKey]
    }));
  }, []);

  const handleToggleGasPipelinesMaster = useCallback(async () => {
    const currentlyOn = layerVisibility[GAS_PIPELINES_MASTER_LAYER_KEY] === true;
    if (currentlyOn) {
      setLayerVisibility((prev) => ({ ...prev, [GAS_PIPELINES_MASTER_LAYER_KEY]: false }));
      return;
    }

    if (gasFetchedIsoRef.current === countryIso && gasPipelinesData?.pipelines?.length) {
      gasLayersBuiltForIsoRef.current = null;
      setLayerVisibility((prev) => {
        const next = { ...prev, [GAS_PIPELINES_MASTER_LAYER_KEY]: true };
        getGasStatusLayerKeys(gasPipelinesData.statistics).forEach((layerKey) => {
          if (next[layerKey] === undefined) {
            next[layerKey] = true;
          }
        });
        return next;
      });
      return;
    }

    if (gasLoading || !countryIso) return;

    setGasLoading(true);
    try {
      const response = await gasPipelinesAPI.getGasPipelines(countryIso);
      if (!response?.success) {
        toast.error(response?.error || 'No gas pipeline data for this country');
        return;
      }

      setGasPipelinesData(response.data);
      gasFetchedIsoRef.current = countryIso;
      gasLayersBuiltForIsoRef.current = null;

      const statusKeys = getGasStatusLayerKeys(response.data.statistics);
      setLayerVisibility((prev) => {
        const next = { ...prev, [GAS_PIPELINES_MASTER_LAYER_KEY]: true };
        statusKeys.forEach((layerKey) => {
          next[layerKey] = true;
        });
        return next;
      });

      const count = response.data?.statistics?.total_pipelines ?? 0;
      toast.success(`Loaded ${count} gas pipeline${count === 1 ? '' : 's'}`);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to load gas pipelines';
      toast.error(msg);
    } finally {
      setGasLoading(false);
    }
  }, [countryIso, gasLoading, gasPipelinesData, layerVisibility]);

  const togglePowerPlantsLayer = useCallback(() => {
    setLayerVisibility(prev => {
      if (!fuelSubtypeLayerKeys.length) {
        return {
          ...prev,
          powerPlants: !prev.powerPlants
        };
      }

      const allVisible = fuelSubtypeLayerKeys.every(key => prev[key] !== false);
      const nextVisible = !allVisible;
      const next = { ...prev, powerPlants: nextVisible };
      fuelSubtypeLayerKeys.forEach(key => {
        next[key] = nextVisible;
      });
      return next;
    });
  }, [fuelSubtypeLayerKeys]);

  const togglePowerPlantSubtypeLayer = useCallback((fuelLayerKey) => {
    setLayerVisibility(prev => {
      const next = {
        ...prev,
        [fuelLayerKey]: !prev[fuelLayerKey]
      };
      if (fuelSubtypeLayerKeys.length) {
        const anyVisible = fuelSubtypeLayerKeys.some(key => next[key]);
        next.powerPlants = anyVisible;
      }
      return next;
    });
  }, [fuelSubtypeLayerKeys]);

  /** Right-click fuel eye: isolate that fuel (parity with Hourly chart legend right-click). Second right-click when only that fuel is visible restores all. */
  const isolatePowerPlantFuel = useCallback((fuelLayerKey) => {
    setLayerVisibility(prev => {
      if (!fuelSubtypeLayerKeys.length) {
        return prev;
      }
      const visibleKeys = fuelSubtypeLayerKeys.filter(key => prev[key] !== false);
      const onlyThisVisible =
        visibleKeys.length === 1 && visibleKeys[0] === fuelLayerKey;

      const next = { ...prev };

      if (onlyThisVisible) {
        fuelSubtypeLayerKeys.forEach(key => {
          next[key] = true;
        });
        next.powerPlants = true;
      } else {
        fuelSubtypeLayerKeys.forEach(key => {
          next[key] = key === fuelLayerKey;
        });
        next.powerPlants = true;
      }
      return next;
    });
  }, [fuelSubtypeLayerKeys]);

  /** Right-click capacity-bin eye: isolate that bin among line layers only. Second right-click restores all capacity layers. */
  const isolateCapacityBinLayer = useCallback((layerKey) => {
    setLayerVisibility(prev => {
      if (!capacityLineLayerKeys.length) {
        return prev;
      }
      const visibleKeys = capacityLineLayerKeys.filter(key => prev[key] !== false);
      const onlyThisVisible =
        visibleKeys.length === 1 && visibleKeys[0] === layerKey;

      const next = { ...prev };

      if (onlyThisVisible) {
        capacityLineLayerKeys.forEach(key => {
          next[key] = true;
        });
      } else {
        capacityLineLayerKeys.forEach(key => {
          next[key] = key === layerKey;
        });
      }
      return next;
    });
  }, [capacityLineLayerKeys]);

  const handleClustersChange = useCallback((newClusters) => {
    setClusters(newClusters);
  }, []);

  const handleRetryLoad = useCallback(async () => {
    const iso = (countryIso || '').toUpperCase();
    if (!iso) return;

    activeTransmissionLoadIsoRef.current = iso;
    lastCompletedTransmissionLoadKeyRef.current = '';

    try {
      setLoading(true);
      setError(null);
      setGenerationError(null);
      setTransmissionData(null);
      setNetworkData(null);
      setGenerationData(null);

      const populationResponse = await transmissionAPI.getTransmissionData(iso, clusters);
      if (!isActiveTransmissionLoad(iso)) return;

      if (!populationResponse.success) {
        throw new Error(populationResponse.error || 'Failed to load population data');
      }
      setTransmissionData(populationResponse.data);

      const [generationResult, networkResult] = await Promise.allSettled([
        transmissionAPI.getTransmissionGenerationData(iso),
        transmissionAPI.getTransmissionNetworkData(iso),
      ]);

      if (!isActiveTransmissionLoad(iso)) return;

      if (generationResult.status === 'fulfilled' && generationResult.value?.success) {
        setGenerationData(generationResult.value.data);
      } else {
        const msg = generationResult.status === 'rejected'
          ? (generationResult.reason?.message || 'Network error')
          : (generationResult.value?.error || 'Generation data unavailable');
        setGenerationData(null);
        setGenerationError(msg);
      }

      if (networkResult.status === 'fulfilled' && networkResult.value?.success) {
        setNetworkData(networkResult.value.data);
      } else {
        setNetworkData(null);
      }

      lastCompletedTransmissionLoadKeyRef.current = `${iso}:${clusters}`;

      toast.success(
        generationResult.status !== 'fulfilled' || !generationResult.value?.success
          ? 'Demand data loaded (generation data unavailable)'
          : 'All data loaded successfully'
      );
    } catch (err) {
      if (isActiveTransmissionLoad(iso)) {
        setError(err.message);
        toast.error(`Failed to load data: ${err.message}`);
        console.error('Error loading data:', err);
      }
    } finally {
      if (isActiveTransmissionLoad(iso)) {
        setLoading(false);
      }
    }
  }, [countryIso, clusters, isActiveTransmissionLoad]);

  const handleClusterChange = useCallback((e) => {
    handleClustersChange(parseInt(e.target.value, 10));
  }, [handleClustersChange]);
  
  /* CLUSTER SELECTOR */
  const ClusterSelector = useMemo(() => (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
      <label className="text-xs text-gray-600 shrink-0">Clusters:</label>
      <select
        value={clusters}
        onChange={handleClusterChange}
        className="pl-2 pr-6 py-1 border border-gray-300 rounded text-xs min-w-0 max-w-full"
      >
        {clusterOptions.map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
    </div>
  ), [clusters, clusterOptions, handleClusterChange]);

  /* DEMAND CLUSTER MAP CARD */
  const DemandClusterMapCard = useMemo(() => (
    <TransmissionLineMapCard
      title="Demand Cluster"
      description="Demand Cluster Map - population regions with transmission lines colored by capacity (MVA)"
      headerControls={ClusterSelector}
    >
      <div className="relative w-full h-full">
        <div ref={mapRef} className="w-full h-full" />
        <TransmissionLineCapacityMapLegend capacityBinSpec={capacityBinSpec} />
      </div>
    </TransmissionLineMapCard>
  ), [ClusterSelector, mapRef, capacityBinSpec]);

  /* GENERATION CLUSTER MAP CARD */
  const GenerationClusterMapCard = useMemo(() => (
    <TransmissionLineMapCard
      title="Generation"
      description="Generation map - power plants with transmission lines colored by capacity (MVA)"
    >
      <div className="relative w-full h-full">
        <div ref={mapRef2} className="w-full h-full" />
        <TransmissionLineCapacityMapLegend capacityBinSpec={capacityBinSpec} />
        {generationError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50/90 rounded-b-xl z-10">
            <div className="text-center px-6">
              <AlertCircle className="h-7 w-7 text-red-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-red-500 mb-1">Generation data unavailable</p>
              <p className="text-xs text-gray-500 max-w-xs">{generationError}</p>
            </div>
          </div>
        )}
      </div>
    </TransmissionLineMapCard>
  ), [generationError, mapRef2, capacityBinSpec]);

  /* MAP STYLE OPTIONS */
  const mapStyleOptions = useMemo(() => (
    Object.values(MAP_STYLES)
  ), []);

  /* HANDLE MAP STYLE SELECT */
  const handleMapStyleSelect = useCallback((e) => {
    const selectedStyle =
      mapStyleOptions.find(
        style => style.name === e.target.value
      );

    if (selectedStyle) {
      handleMapStyleChange(selectedStyle);
    }
  }, [mapStyleOptions, handleMapStyleChange]);

  /* MAP STYLE SELECTOR */
  const MapStyleSelector = useMemo(() => (
    <div className="mb-3 md:mb-4 pb-2 md:pb-3 border-b border-gray-100 w-full min-w-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2 min-w-0">
        <Settings className="h-4 w-4 text-gray-600 shrink-0" />
        <label className="text-sm text-gray-600 shrink-0">Map Style:</label>
        <select
          value={mapStyle?.name}
          onChange={handleMapStyleSelect}
          className="text-sm border border-gray-300 rounded pl-3 pr-7 py-1 bg-white min-w-0 max-w-full sm:max-w-xs"
        >
          {mapStyleOptions.map(style => (
            <option
              key={style.name}
              value={style.name}
            >
              {style.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  ), [mapStyle?.name, mapStyleOptions, handleMapStyleSelect]);

  /* DEMAND CLUSTER LEGEND */
  const DemandClusterLegend = useMemo(() => (
    <div className="space-y-2 min-w-0">
      <p className="text-sm font-medium text-gray-700 mb-2">
        Demand Cluster Only
      </p>
      <div className="h-0.5 bg-gray-500/45 mb-2 mr-4 md:mr-8 lg:mr-12" />
  
      {/* Demand Points */}
      <LayerVisibilityRow
        checked={layerVisibility?.populationPoints !== false}
        onToggle={() => toggleLayer('populationPoints')}
        ariaLabel="Toggle Demand Points visibility"
      >
        <div className="w-3 h-3 bg-gray-400 rounded-full" />
        <span className="text-gray-700">Demand Points</span>
        <span className="text-gray-500">({summary?.total_demand_points || 0})</span>
      </LayerVisibilityRow>

      {/* Cluster Centers */}
      <LayerVisibilityRow
        checked={layerVisibility?.clusterCenters !== false}
        onToggle={() => toggleLayer('clusterCenters')}
        ariaLabel="Toggle Cluster Centers visibility"
      >
        <div className="w-3 h-3 bg-gray-600" style={{ borderRadius: '2px' }} />
        <span className="text-gray-700">Cluster Centers</span>
        <span className="text-gray-500">({summary?.total_regions || 0})</span>
      </LayerVisibilityRow>
      </div>
    ), [summary, layerVisibility, toggleLayer]);

  const handleToggleCapacityBinLayer = useCallback((layerKey) => {
    toggleLayer(layerKey);
  }, [toggleLayer]);

  const CapacityBinsLegend = useMemo(() => {
    const belowP25Label = formatBelowP25LegendLabel(capacityBinSpec);
    const nullExcludedLabel = formatNullExcludedLegendLabel(capacityBinSpec);

    if (!capacityBinSpec?.hasData && !belowP25Label && !nullExcludedLabel) {
      return (
        <p className="text-xs text-gray-500 pr-4 md:pr-12">No transmission line capacity data</p>
      );
    }

    return (
      <>
        {capacityBinSpec.bins?.map((bin) => (
            <LayerVisibilityRow
              key={bin.layerKey}
              checked={layerVisibility[bin.layerKey] !== false}
              onToggle={() => handleToggleCapacityBinLayer(bin.layerKey)}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                isolateCapacityBinLayer(bin.layerKey);
              }}
              ariaLabel={`Toggle ${bin.rangeLabel} visibility`}
            >
              <span
                className="w-4 h-0.5 shrink-0 rounded-full"
                style={{ backgroundColor: bin.color }}
              />
              <span className="text-gray-700 truncate" title={bin.rangeLabel}>
                {bin.rangeLabel}
              </span>
              <span className="text-gray-500 shrink-0">({bin.count})</span>
            </LayerVisibilityRow>
          ))}
        {belowP25Label && (
          <LayerVisibilityRow
            checked={layerVisibility[CAPACITY_FILTERED_LAYER_KEY] !== false}
            onToggle={() => handleToggleCapacityBinLayer(CAPACITY_FILTERED_LAYER_KEY)}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              isolateCapacityBinLayer(CAPACITY_FILTERED_LAYER_KEY);
            }}
            ariaLabel="Toggle below P25 length lines visibility"
          >
            <span
              className="w-4 h-0.5 shrink-0 rounded-full"
              style={{ backgroundColor: FILTERED_LINE_COLOR, opacity: 0.85 }}
            />
            <span className="text-gray-700">{belowP25Label}</span>
          </LayerVisibilityRow>
        )}
        {nullExcludedLabel && (
          <LayerVisibilityRow
            checked={layerVisibility[CAPACITY_FILTERED_LAYER_KEY] !== false}
            onToggle={() => handleToggleCapacityBinLayer(CAPACITY_FILTERED_LAYER_KEY)}
            ariaLabel="Toggle missing capacity or length lines visibility"
          >
            <span
              className="w-4 h-0.5 shrink-0 rounded-full"
              style={{ backgroundColor: FILTERED_LINE_COLOR, opacity: 0.85 }}
            />
            <span className="text-gray-700">{nullExcludedLabel}</span>
          </LayerVisibilityRow>
        )}
      </>
    );
  }, [
    capacityBinSpec,
    handleToggleCapacityBinLayer,
    isolateCapacityBinLayer,
    layerVisibility,
  ]);

  /* GENERATION CLUSTER LEGEND */
  const GenerationClusterLegend = useMemo(() => (
    <div className="space-y-2 min-w-0">
      <p className="text-sm font-medium text-gray-700 mb-2">
        Generation Cluster Only
      </p>
      <div className="h-0.5 bg-gray-500/45 mb-2 mr-4 md:mr-8 lg:mr-12" />
      <LayerVisibilityRow
        checked={layerVisibility?.powerPlants !== false}
        onToggle={togglePowerPlantsLayer}
        ariaLabel="Toggle Power Plants visibility"
      >
        <div className="w-3 h-3 bg-blue-500 rounded-full" />
        <span className="text-gray-700">Power Plants</span>
        <span className="text-gray-500">({generationStats?.total_plants || 0})</span>
      </LayerVisibilityRow>
      {availableFuelTypes.length > 0 && (
        <div className="pt-1 pl-3 pr-4 sm:pl-5 sm:pr-8 md:pr-12 min-w-0">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-6 md:gap-x-12 xl:gap-x-20 gap-y-0 min-w-0">
            {[availableFuelTypes.slice(0, 5), availableFuelTypes.slice(5)].map((fuelColumn, columnIndex) => (
              <div key={`fuel-column-${columnIndex}`} className="space-y-1.5">
                {fuelColumn.map((fuelType) => {
                  const fuelLayerKey = getFuelLayerKey(fuelType);
                  const isVisible = layerVisibility?.[fuelLayerKey] !== false;
                  return (
                    <LayerVisibilityRow
                      key={fuelLayerKey}
                      className="pr-2 md:pr-12"
                      checked={isVisible}
                      onToggle={() => togglePowerPlantSubtypeLayer(fuelLayerKey)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        isolatePowerPlantFuel(fuelLayerKey);
                      }}
                      ariaLabel={`Toggle ${fuelType} visibility`}
                    >
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: getFuelColorFromCache(fuelType) || '#7F8C8D' }}
                      />
                      <span className="text-gray-600">{fuelType}</span>
                    </LayerVisibilityRow>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  ), [availableFuelTypes, generationStats?.total_plants, isolatePowerPlantFuel, layerVisibility, togglePowerPlantSubtypeLayer, togglePowerPlantsLayer]);

  /* TRANMISSION LINE DOCUMENTATION ACCORDION COMPONENT */
  const TransmissionLineDocumentationAccordionComponent = useMemo(() => {
    return <TransmissionLineDocumentationAccordion />;
  }, []);

  /* LOADING STATE */
  if (loading) {
    return <LoadingState />;
  }

  /* ERROR STATE */
  if (error) {
    return <ErrorState message={error} onRetry={handleRetryLoad} />;
  }

  /* NO TRANSMISSION DATA STATE */
  if (!transmissionData) {
    return <NoTransmissionDataState />;
  }

  return (
    <div className="w-full min-w-0 min-h-screen flex flex-col overflow-x-hidden overflow-y-auto px-2 md:px-4 lg:px-6 py-2 md:py-4 lg:py-6">
      <div className="w-full min-w-0">
        {/* Maps - Clean Card Structure */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-3 md:mb-4 min-w-0">
                    
          {/* Demand Cluster Map Card */}
          {DemandClusterMapCard}
          
          {/* Generation Cluster Map Card */}
          {GenerationClusterMapCard}
          
        </div>
      </div>

      {/* Map Layers Control Section */}
      <div className="bg-white rounded-lg hover:shadow-sm transition-shadow w-full min-w-0 overflow-hidden p-3 sm:p-4 md:p-4">
        <div className="flex items-center gap-2 mb-2 md:mb-3 min-w-0">
          <Layers className="h-5 w-5 text-gray-600 shrink-0" />
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">Map Layers Control</h3>
        </div>
        
        {/* Map Style Selector */}
        {MapStyleSelector}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4 md:gap-x-8 lg:gap-x-12 text-sm min-w-0">
          {/* Demand Cluster Layers */}
          {DemandClusterLegend}

          {/* Transmission Infrastructure Layers */}
          <div className="space-y-2 min-w-0">
            <p className="text-sm font-medium text-gray-700 mb-2">Both Maps</p>
            <div className="h-0.5 bg-gray-500/45 mb-2 mr-4 md:mr-8 lg:mr-12" />
            
            <LayerVisibilityRow
              checked={layerVisibility.transmissionBuses !== false}
              onToggle={() => toggleLayer('transmissionBuses')}
              ariaLabel="Toggle Transmission Buses visibility"
            >
              <div className="w-3 h-3 bg-yellow-500 rounded-full" />
              <span className="text-gray-700">Transmission Buses</span>
              <span className="text-gray-500">({networkStats.total_buses || 0})</span>
            </LayerVisibilityRow>
            
            <p className="text-xs text-gray-500 mb-1 pr-4 md:pr-12">
              {capacityBinSpec.mode === 'discrete'
                ? `Each distinct ${TRANSMISSION_CAPACITY_UNIT} class has its own color (eligible lines only)`
                : `Adjacent ${TRANSMISSION_CAPACITY_UNIT} values grouped; short/null lines in grey`}
            </p>
            {CapacityBinsLegend}

            <div className="h-0.5 bg-gray-500/45 my-2 mr-4 md:mr-8 lg:mr-12" />
            <p className="text-xs text-gray-500 mb-1 pr-4 md:pr-12">
              Operating and construction only (GEM-GGIT). Loaded on first enable.
            </p>
            <LayerVisibilityRow
              checked={layerVisibility[GAS_PIPELINES_MASTER_LAYER_KEY] === true}
              onToggle={handleToggleGasPipelinesMaster}
              ariaLabel="Toggle gas pipelines layer"
            >
              <span className="w-4 h-0.5 shrink-0 rounded-full bg-emerald-500" />
              <span className="text-gray-700">Gas Pipelines</span>
              <span className="text-gray-500">
                ({gasLoading ? '…' : (gasPipelineStats.total_pipelines ?? '—')})
              </span>
            </LayerVisibilityRow>
            {layerVisibility[GAS_PIPELINES_MASTER_LAYER_KEY] === true && gasStatusLayerKeys.map((layerKey) => {
              const status = layerKey.replace('gasPipelines__', '');
              const count = gasPipelineStats.by_status?.[status] ?? 0;
              const color = GAS_PIPELINE_STATUS_COLORS[status] || '#34495e';
              const label = status.charAt(0).toUpperCase() + status.slice(1);
              return (
                <LayerVisibilityRow
                  key={layerKey}
                  className="pl-2"
                  checked={layerVisibility[layerKey] !== false}
                  onToggle={() => toggleLayer(layerKey)}
                  ariaLabel={`Toggle ${label} gas pipelines`}
                >
                  <span
                    className="w-4 h-0.5 shrink-0 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-gray-700">{label}</span>
                  <span className="text-gray-500">({count})</span>
                </LayerVisibilityRow>
              );
            })}
          </div>

          {/* Generation Cluster Layers */}
          {GenerationClusterLegend}
        </div>
      </div>
  
      {/* Accordion Component for Documentation */}
      {TransmissionLineDocumentationAccordionComponent}
      
    </div>
  );
};

export default TransmissionLineTab;

